import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Heart, Leaf, Coffee, Users, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import SEO, { SEO_CONFIG } from '@/components/SEO';
import api from '@/utils/apiClient';
import { GoogleReviewCardFull } from '@/components/GoogleReviewCard';

const STORY_IMAGE = "https://images.pexels.com/photos/974400/pexels-photo-974400.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const TEAM_IMAGE = "https://images.pexels.com/photos/302897/pexels-photo-302897.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const BEANS_IMAGE = "https://images.unsplash.com/photo-1773316446914-7ea032d2fd8c?crop=entropy&cs=srgb&fm=jpg&q=85";
const PHILOSOPHY_IMAGE = "https://images.pexels.com/photos/1640777/pexels-photo-1640777.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

// Default content - used if CMS content is not available
const defaultContent = {
  hero: {
    tagline: "Our Story",
    title: "Born from a Love of Fiji & Coffee",
    description: "Bulaccino Cafe was born from a simple dream: to create a space where the warmth of Fijian hospitality meets world-class coffee and cuisine."
  },
  beginning: {
    title: "The Beginning",
    paragraphs: [
      "In the heart of Nadi, where tropical breezes carry the scent of frangipani and the sounds of laughter fill the air, Bulaccino Cafe found its home. What started as a small dream has blossomed into Fiji's most beloved café destination.",
      "Our founders, passionate about both coffee culture and Fijian traditions, envisioned a place where travelers could experience authentic island flavors while locals could find their daily sanctuary.",
      "The name \"Bulaccino\" is a playful fusion of \"Bula\" — the Fijian word for hello, life, and love — and \"cappuccino,\" representing our commitment to exceptional coffee craftsmanship."
    ]
  },
  philosophy: {
    tagline: "Our Philosophy",
    title: "Full Body Nourishment",
    paragraphs: [
      "To provide food and coffee that nourishes the body and mind. We use the freshest, high quality ingredients often from the Bulaccino Organic farm.",
      "Our food is made with love and served in a unique & pleasant environment. Enjoy the real food experience and our Fijian hospitality!"
    ]
  },
  values: {
    tagline: "What We Believe",
    title: "Our Values",
    items: [
      { icon: "Leaf", title: "Farm to Table", description: "We source our ingredients from local Fijian farms, supporting our community while ensuring the freshest flavors on your plate." },
      { icon: "Coffee", title: "Craft Coffee", description: "Our baristas are trained in the art of coffee, bringing you perfectly crafted beverages from ethically sourced beans." },
      { icon: "Heart", title: "Made with Love", description: "Every dish, every drink is prepared with genuine care and passion. We treat every guest like family." },
      { icon: "Users", title: "Community First", description: "Bulaccino Cafe is more than a café — it's a gathering place for locals and travelers to connect over great food." }
    ]
  },
  coffee: {
    title: "Our Coffee Journey",
    paragraphs: [
      "We believe great coffee tells a story. That's why we've partnered with ethical coffee growers who share our commitment to quality and sustainability.",
      "Each bean is carefully selected and roasted to bring out its unique character. Our signature Bulaccino Cafe blend combines smooth South American notes with bright Pacific island undertones — a true taste of paradise in every cup.",
      "From the first pour to the last sip, we ensure every coffee moment at Bulaccino Cafe is extraordinary."
    ]
  },
  team: {
    title: "Meet Our Team",
    paragraphs: [
      "Behind every great cup and every delicious dish is our incredible team. From our passionate baristas to our creative kitchen crew, everyone at Bulaccino Cafe shares one common goal: to make your visit unforgettable.",
      "Many of our team members are born and raised in Fiji, bringing authentic local knowledge and that famous Fijian warmth to everything we do. They're not just employees — they're the heart and soul of Bulaccino Cafe."
    ]
  },
  cta: {
    title: "Experience the Bulaccino Cafe Difference",
    description: "Join us for a cup, a meal, or simply a moment of tropical tranquility. We can't wait to welcome you."
  }
};

const iconMap = { Leaf, Coffee, Heart, Users };

// Parse CMS HTML content into structured sections
const parseCMSContent = (html) => {
  if (!html) return null;
  
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const content = {};
    
    // Extract hero section
    const heroSection = doc.querySelector('.hero-section');
    if (heroSection) {
      content.hero = {
        tagline: heroSection.querySelector('.tagline')?.textContent?.trim() || defaultContent.hero.tagline,
        title: heroSection.querySelector('h1')?.textContent?.trim() || defaultContent.hero.title,
        description: heroSection.querySelector('p:not(.tagline)')?.textContent?.trim() || defaultContent.hero.description
      };
    }
    
    // Extract all h2 sections and their following paragraphs
    const allH2s = doc.querySelectorAll('h2');
    allH2s.forEach(h2 => {
      const title = h2.textContent?.trim().toLowerCase();
      const paragraphs = [];
      let sibling = h2.nextElementSibling;
      
      while (sibling && sibling.tagName !== 'H2' && !sibling.classList?.contains('hero-section')) {
        if (sibling.tagName === 'P') {
          paragraphs.push(sibling.textContent?.trim());
        }
        sibling = sibling.nextElementSibling;
      }
      
      if (title?.includes('beginning')) {
        content.beginning = { title: h2.textContent?.trim(), paragraphs };
      } else if (title?.includes('philosophy') || title?.includes('nourishment')) {
        content.philosophy = { 
          tagline: "Our Philosophy",
          title: h2.textContent?.trim(), 
          paragraphs 
        };
      } else if (title?.includes('coffee')) {
        content.coffee = { title: h2.textContent?.trim(), paragraphs };
      } else if (title?.includes('team')) {
        content.team = { title: h2.textContent?.trim(), paragraphs };
      } else if (title?.includes('values')) {
        content.values = { tagline: "What We Believe", title: h2.textContent?.trim() };
      }
    });
    
    // Extract philosophy section if it has a tagline
    const philosophyTagline = doc.querySelector('p.tagline:not(.hero-section .tagline)');
    if (philosophyTagline && content.philosophy) {
      content.philosophy.tagline = philosophyTagline.textContent?.trim();
    }
    
    // Extract features/values
    const features = doc.querySelectorAll('.feature');
    if (features.length > 0) {
      content.values = content.values || { tagline: "What We Believe", title: "Our Values" };
      content.values.items = Array.from(features).map((f, i) => ({
        icon: defaultContent.values.items[i]?.icon || "Coffee",
        title: f.querySelector('h3')?.textContent?.trim() || "",
        description: f.querySelector('p')?.textContent?.trim() || ""
      }));
    }
    
    // Extract CTA section
    const ctaSection = doc.querySelector('.cta-section');
    if (ctaSection) {
      content.cta = {
        title: ctaSection.querySelector('h2')?.textContent?.trim() || defaultContent.cta.title,
        description: ctaSection.querySelector('p')?.textContent?.trim() || defaultContent.cta.description
      };
    }
    
    return content;
  } catch (e) {
    console.error('Error parsing CMS content:', e);
    return null;
  }
};

export default function AboutPage() {
  const [content, setContent] = useState(defaultContent);
  const [seoData, setSeoData] = useState(null);

  useEffect(() => {
    // Fetch CMS content for editable text
    api.get('/simple-cms/pages/about')
      .then(res => {
        if (res.data.seo) setSeoData(res.data.seo);
        const parsed = parseCMSContent(res.data.content);
        if (parsed) {
          // Merge parsed content with defaults (parsed takes priority)
          setContent(prev => ({
            hero: { ...prev.hero, ...parsed.hero },
            beginning: { ...prev.beginning, ...parsed.beginning },
            philosophy: { ...prev.philosophy, ...parsed.philosophy },
            values: { ...prev.values, ...parsed.values },
            coffee: { ...prev.coffee, ...parsed.coffee },
            team: { ...prev.team, ...parsed.team },
            cta: { ...prev.cta, ...parsed.cta }
          }));
        }
      })
      .catch(err => console.log('Using default content'));
  }, []);

  const values = content.values?.items || defaultContent.values.items;

  return (
    <div className="pt-24 pb-16 overflow-x-hidden" data-testid="about-page">
      <SEO 
        title={seoData?.title || SEO_CONFIG.about.title}
        description={seoData?.description || SEO_CONFIG.about.description}
        path="/about" 
      />
      
      {/* Hero */}
      <section className="relative py-24 md:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-secondary/50 to-transparent" />
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
          <div className="max-w-3xl">
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-primary font-medium mb-4"
            >
              {content.hero.tagline}
            </motion.p>
            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="font-heading text-5xl md:text-6xl lg:text-7xl text-foreground mb-6"
            >
              {content.hero.title.includes('Fiji') ? (
                <>
                  {content.hero.title.split('Fiji')[0]}
                  <span className="text-primary">Fiji</span>
                  {content.hero.title.split('Fiji')[1]?.replace('&', ' &').split('Coffee')[0]}
                  <span className="text-accent">Coffee</span>
                  {content.hero.title.split('Coffee')[1] || ''}
                </>
              ) : (
                content.hero.title
              )}
            </motion.h1>
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-lg text-muted-foreground leading-relaxed"
            >
              {content.hero.description}
            </motion.p>
          </div>
        </div>
      </section>

      {/* Story Section - The Beginning */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="relative"
            >
              <img 
                src={STORY_IMAGE}
                alt="Bulaccino Cafe Interior"
                className="rounded-3xl shadow-2xl w-full"
              />
              <div className="absolute -bottom-6 -right-6 w-48 h-48 bg-primary/10 rounded-full blur-3xl -z-10" />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="font-heading text-4xl mb-6">{content.beginning.title}</h2>
              <div className="space-y-4 text-muted-foreground leading-relaxed">
                {content.beginning.paragraphs.map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Our Philosophy Section */}
      <section className="py-16 md:py-24 bg-muted/30">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="order-2 lg:order-1"
            >
              <p className="text-primary font-medium mb-3">{content.philosophy.tagline}</p>
              <h2 className="font-heading text-4xl mb-6">{content.philosophy.title}</h2>
              <div className="space-y-4 text-muted-foreground leading-relaxed">
                {content.philosophy.paragraphs.map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="order-1 lg:order-2 relative"
            >
              <img 
                src={PHILOSOPHY_IMAGE}
                alt="Fresh healthy food at Bulaccino Cafe"
                className="rounded-3xl shadow-2xl w-full"
              />
              <div className="absolute -top-6 -right-6 w-32 h-32 bg-primary/10 rounded-full blur-2xl -z-10" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="text-primary font-medium mb-3">{content.values.tagline}</p>
            <h2 className="font-heading text-4xl md:text-5xl text-foreground">
              {content.values.title}
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {values.map((value, i) => {
              const IconComponent = iconMap[value.icon] || Coffee;
              return (
                <motion.div
                  key={value.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-card p-8 rounded-3xl shadow-lg"
                  data-testid={`value-${i}`}
                >
                  <div className="w-14 h-14 rounded-2xl bg-secondary flex items-center justify-center mb-6">
                    <IconComponent className="w-7 h-7 text-primary" />
                  </div>
                  <h3 className="font-heading text-2xl mb-3">{value.title}</h3>
                  <p className="text-muted-foreground leading-relaxed">{value.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Coffee Section */}
      <section className="py-16 md:py-24 bg-muted/30">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="order-2 lg:order-1"
            >
              <h2 className="font-heading text-4xl mb-6">{content.coffee.title}</h2>
              <div className="space-y-4 text-muted-foreground leading-relaxed">
                {content.coffee.paragraphs.map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="order-1 lg:order-2 relative"
            >
              <img 
                src={BEANS_IMAGE}
                alt="Coffee Beans"
                className="rounded-3xl shadow-2xl w-full"
              />
              <div className="absolute -top-6 -left-6 w-32 h-32 bg-accent/10 rounded-full blur-2xl -z-10" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <img 
                src={TEAM_IMAGE}
                alt="Bulaccino Team"
                className="rounded-3xl shadow-2xl w-full"
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="font-heading text-4xl mb-6">{content.team.title}</h2>
              <div className="space-y-4 text-muted-foreground leading-relaxed">
                {content.team.paragraphs.map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
              </div>
              <Link to="/contact" className="inline-block mt-8" data-testid="about-contact-btn">
                <Button className="rounded-full bg-primary text-white hover:bg-primary/90 px-8">
                  Come Say Hello
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 bg-muted/30">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Text Content */}
            <div className="text-center lg:text-left">
              <h2 className="font-heading text-4xl md:text-5xl text-foreground mb-4">
                {content.cta.title}
              </h2>
              <p className="text-muted-foreground max-w-xl mx-auto lg:mx-0 mb-8">
                {content.cta.description}
              </p>
              <div className="flex flex-wrap justify-center lg:justify-start gap-4">
                <Link to="/menu" data-testid="about-menu-btn">
                  <Button className="rounded-full bg-primary text-white hover:bg-primary/90 px-8">
                    Explore Our Menu
                  </Button>
                </Link>
                <Link to="/order" data-testid="about-order-btn">
                  <Button variant="outline" className="rounded-full border-2 border-primary/20 hover:border-primary px-8">
                    Order Online
                  </Button>
                </Link>
              </div>
            </div>
            
            {/* Google Reviews Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="max-w-sm mx-auto lg:mx-0 lg:ml-auto"
            >
              <GoogleReviewCardFull />
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
